/**
 * File uploads (raw body → POST /api/uploads). Files can't go through the JSON
 * @api door, so this is the one shared exception. Same-origin, the staff JWT
 * cookie authorises it; the server stores the file and returns
 * { media_id, url } where `url` is a short-lived signed link. `alt` is the file's
 * description (required for an illustrative unit image, MERCH-77).
 */
export async function uploadFile(file, { ownerType, ownerId, kind, alt }) {
  // No kind: the server stores the owner's defaultKind (lib/media/upload OWNERS).
  const qs = new URLSearchParams({ owner_type: ownerType, owner_id: String(ownerId), ...(kind ? { kind } : {}), ...(alt ? { alt } : {}) });
  const res = await fetch(`/api/uploads?${qs}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || 'upload') },
    body: file,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
  return body.data;
}

/**
 * Upload links (MERCH-59, MERCH-78): the page an assistant hands a person (/admin/upload#<token>).
 * The token is the only credential, so both calls omit cookies; they use fetch directly because
 * the @api registry speaks JSON and the PUT body is the raw file. Errors carry the server's
 * `status`, `code` and message (404 = the link expired, was used or is not this store's).
 */
const linkPath = (token) => `/api/uploads/t/${encodeURIComponent(token)}`;

async function linkError(res) {
  const body = await res.json().catch(() => ({}));
  const err = new Error(body.error || `The upload failed (${res.status})`);
  err.status = res.status;
  err.code = body.code;
  return err;
}

/** What the link is for: { kind, owner_type, owner_label, accepted_mimes, max_bytes, alt_required, expires_at, review_status }. */
export async function describeUploadLink(token) {
  const res = await fetch(linkPath(token), { method: 'GET', credentials: 'omit', cache: 'no-store' });
  if (!res.ok) throw await linkError(res);
  return (await res.json()).data;
}

/** PUT the raw file to the link: { media_id, url, review_status, … }. `attested`: the genuine-photo confirmation. */
export async function sendToUploadLink(token, file, { contentType, alt, attested } = {}) {
  const headers = { 'Content-Type': contentType || file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || 'upload') };
  if (alt) headers['X-Alt-Text'] = encodeURIComponent(alt);
  // A unit photo link: the person confirmed the photo is genuine (the server refuses the PUT otherwise).
  if (attested) headers['X-Attest-Genuine'] = '1';
  const res = await fetch(linkPath(token), { method: 'PUT', credentials: 'omit', headers, body: file });
  if (!res.ok) throw await linkError(res);
  return (await res.json()).data;
}
